import { useCallback, useRef, useState } from "react";

import { DirectoryEntitySearchDuplicateDto } from "@/ContactDirectory/Api/ClientParameters/DirectoryEntityDto";
import { DirectoryEntityType } from "@/ContactDirectory/Api/medfar-contactdirectory/dist";
import { useDirectoryClientForType } from "@/ContactDirectory/Hooks/useDirectoryClientForType";
import { DirectoryHealthCareProvider } from "@/ContactDirectory/Models/DirectoryHealthCareProvider";
import { DirectoryOtherEstablishment } from "@/ContactDirectory/Models/DirectoryOtherEstablishment";
import { DirectoryPharmacy } from "@/ContactDirectory/Models/DirectoryPharmacy";
import { DuplicateSearchType } from "@/ContactDirectory/Models/enums/DuplicateSearchType";
import { IDirectoryEntity } from "@/ContactDirectory/Models/IDirectoryEntity";
import { FaxDirectorySearchResultItem } from "../Search/FaxDirectorySearchResultItem";
import { IDuplicateWarningPopupProps } from "./DuplicateWarningPopup";

interface UseDuplicateWarningPopupReturnProps {
    duplicateWarningPopupProps: IDuplicateWarningPopupProps;
    showWarningDuplicateMessage: boolean;
    searchDuplicateForWarning: (currentEntity: IDirectoryEntity, dto: DirectoryEntitySearchDuplicateDto, duplicateSearchType: DuplicateSearchType) => Promise<boolean>;
}

function useDuplicateWarningPopupProps(type: DirectoryEntityType): UseDuplicateWarningPopupReturnProps {
    const [duplicateResultState, setDuplicateResultState] = useState<{ duplicateList: FaxDirectorySearchResultItem[], type: DuplicateSearchType }>({ duplicateList: [], type: null });

    const client = useDirectoryClientForType(type, true);

    const lastEntityCheckedSnapshot = useRef<string>("");

    const searchDuplicates = useCallback(
        async (
            currentEntity: IDirectoryEntity,
            dto: DirectoryEntitySearchDuplicateDto,
            duplicateSearchType: DuplicateSearchType
        ): Promise<boolean> => {
            let list: IDirectoryEntity[] = [];
            let modifiedList: IDirectoryEntity[] = list;

            const currentSnapShot = JSON.stringify(currentEntity);
            if (lastEntityCheckedSnapshot.current === currentSnapShot)
                return false;

            const data = await client.searchDuplicates(currentEntity.id, dto, type, duplicateSearchType);
            switch (type) {
                case DirectoryEntityType.HealthcareProvider:
                    list.push(...(data.healthCareProviders ?? []).map(p => DirectoryHealthCareProvider.fromSearchDto(p)));
                    break;
                case DirectoryEntityType.Other:
                    list.push(...(data.otherEstablishments ?? []).map(p => DirectoryOtherEstablishment.fromSearchDto(p)));
                    break;
                case DirectoryEntityType.Pharmacy:
                    list.push(...(data.pharmacies ?? []).map(p => DirectoryPharmacy.fromSearchDto(p)));
                    break;
            }

            modifiedList = list;
            if (!!currentEntity.id && list.any() && list.any(entity => entity.id == currentEntity.id)) {
                modifiedList = list.filter(entity => entity.id != currentEntity.id);
            }

            lastEntityCheckedSnapshot.current = currentSnapShot;

            if (!modifiedList.any())
                return false;

            setDuplicateResultState({
                duplicateList: modifiedList.map(p => new FaxDirectorySearchResultItem({ entity: p })),
                type: duplicateSearchType
            });

            return true;
        },
        [client, type]
    );

    return {
        duplicateWarningPopupProps: {
            onClose: () => setDuplicateResultState({ duplicateList: [], type: null }),
            duplicateList: duplicateResultState.duplicateList,
            duplicateSearchType: duplicateResultState.type
        },
        showWarningDuplicateMessage: duplicateResultState.duplicateList.any() && duplicateResultState.type !== null,
        searchDuplicateForWarning: (currentEntity: IDirectoryEntity, dto: DirectoryEntitySearchDuplicateDto, duplicateSearchType: DuplicateSearchType) => searchDuplicates(currentEntity, dto, duplicateSearchType)
    };
}

export { useDuplicateWarningPopupProps };
